/**
 * Light Preset buttons - the PWA-side state behind OPBridge's built-in
 * lighting presets (`GET /lighting/presets`, `POST /lighting/fire|stop`,
 * OPBridge S287 / DEC-601).
 *
 * Two mirrors and the decisions made from them:
 *
 * - the CATALOGUE, per bridge. Same shape as `program/programs.ts` and
 *   `obs/scenes.ts`: a plain signal over an immutable record (never a Map -
 *   a Solid read of a Map tracks nothing), keyed by bridge ROW id. A preset
 *   plays on the lights patched on the bridge it is sent to, so the list a
 *   button is configured against belongs to that machine.
 * - the LAYERS this app believes are running, per button. OPBridge has no
 *   read of running layers, so this is OUR knowledge: set by a successful
 *   fire, cleared by a stop (a 404 stop included - the layer is gone either
 *   way) and by a Blackout this app sent, which stops every layer on
 *   OPBridge. It cannot see a Blackout pressed elsewhere or a daemon
 *   restart; the next press re-fires and corrects it. Rendered as ON, never
 *   as a "pending" state (the operator's no-readback rule, CP-159).
 */

import { createSignal, type Accessor } from 'solid-js';
import type { DmxTarget } from '@opbuttons/schema';
import type { LightingFireBody, LightingPreset } from '../opbridge/httpClient.ts';
import type { LightPresetConfig } from '../layout/projectStore.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { fixtures } from '../fixtures/store.ts';
import { resolveTarget, targetIsLive } from './targets.ts';

// ─── Catalogue ────────────────────────────────────────────────────────────

/**
 * - `unauthorized` - the bearer lacks the v19 lighting scopes (401/403). The
 *   fix is a re-pair or an OPBridge update, never "pick another preset".
 * - `unsupported` - 404: an OPBridge older than v1.15.154 has no such route.
 * - `error` - anything else (unreachable, parse failure).
 * - `unknown` - never asked. NOT "no presets".
 */
export type PresetListStatus = 'unknown' | 'ok' | 'unauthorized' | 'unsupported' | 'error';

export interface PresetListState {
  status: PresetListStatus;
  /** Empty on any non-`ok` status - never a stale list. */
  presets: LightingPreset[];
  at: number;
  detail?: string;
}

const EMPTY: PresetListState = { status: 'unknown', presets: [], at: 0 };

const [statesSig, setStatesSig] = createSignal<Record<string, PresetListState>>({});

export const presetStates: Accessor<Record<string, PresetListState>> = statesSig;

export function presetState(bridgeRowId: string): PresetListState {
  return statesSig()[bridgeRowId] ?? EMPTY;
}

export function recordPresets(bridgeRowId: string, presets: LightingPreset[]): void {
  setStatesSig((prev) => ({ ...prev, [bridgeRowId]: { status: 'ok', presets, at: Date.now() } }));
}

export function recordPresetsFailure(
  bridgeRowId: string,
  status: Exclude<PresetListStatus, 'ok' | 'unknown'>,
  detail?: string,
): void {
  setStatesSig((prev) => ({
    ...prev,
    [bridgeRowId]: { status, presets: [], at: Date.now(), ...(detail ? { detail } : {}) },
  }));
}

export function presetById(presetId: string | undefined, st: PresetListState): LightingPreset | undefined {
  if (!presetId || st.status !== 'ok') return undefined;
  return st.presets.find((p) => p.id === presetId);
}

export function presetIsPhotosensitive(p: LightingPreset | undefined): boolean {
  return !!p && p.tags.includes('photosensitive');
}

// ─── Running layers (our knowledge) ───────────────────────────────────────

const [layersSig, setLayersSig] = createSignal<Record<string, true>>({});

export function presetLayerRunning(itemId: string): boolean {
  return !!layersSig()[itemId];
}

const endTimers = new Map<string, ReturnType<typeof setTimeout>>();

function setRunning(itemId: string, running: boolean): void {
  setLayersSig((prev) => {
    if (!!prev[itemId] === running) return prev;
    const next = { ...prev };
    if (running) next[itemId] = true; else delete next[itemId];
    return next;
  });
}

/**
 * Record a fire or a stop. A TIMED layer (`lengthBeats`) ends on OPBridge by
 * itself, so pass `endsAfterMs` and the button goes dark when it does rather
 * than claiming a show that has finished. A later fire or stop of the same
 * button replaces the timer.
 */
export function markPresetLayer(itemId: string, running: boolean, endsAfterMs?: number): void {
  const t = endTimers.get(itemId);
  if (t !== undefined) { clearTimeout(t); endTimers.delete(itemId); }
  setRunning(itemId, running);
  if (running && endsAfterMs !== undefined && endsAfterMs > 0) {
    endTimers.set(itemId, setTimeout(() => { endTimers.delete(itemId); setRunning(itemId, false); }, endsAfterMs));
  }
}

/**
 * How long a timed layer lasts, in ms. OPBridge times it on the musical
 * clock when playing and otherwise "free-running at the clock's last tempo,
 * else 120 BPM" (DEC-601) - so the clock's last reported tempo is the right
 * number either way. On a playing clock beat 0 lands on the START of the
 * current bar, so the real end can be up to a bar sooner; the light may
 * outlast the show by at most that, never cut it short.
 */
export function presetLengthMs(lengthBeats: number, lastTempo: number | null): number {
  const bpm = lastTempo && lastTempo > 0 ? lastTempo : 120;
  return (lengthBeats * 60_000) / bpm;
}

/** A Blackout this app sent stops every layer on OPBridge (DEC-601: "Blackout
 *  still stops everything"), so every preset button goes dark with it. */
export function clearAllPresetLayers(): void {
  for (const t of endTimers.values()) clearTimeout(t);
  endTimers.clear();
  setLayersSig({});
}

// ─── Decisions (pure) ─────────────────────────────────────────────────────

/**
 * Why a Light Preset button cannot fire, or null.
 *
 * THE ORDER IS THE CONTENT, same ladder as `programButtonFault`:
 *
 *   no-bridge        no machine chosen, and a preset plays on the machine
 *                    it is sent to - so this outranks no-preset.
 *   no-preset        aimed, nothing chosen.
 *   lights-gone      the chosen group or light no longer exists. Firing
 *                    would light something the operator did not pick (or
 *                    nothing), so it is a fault, not a fallback to the rig.
 *   unauthorized /   we could not read the catalogue, so we cannot know
 *   unsupported /    whether the preset exists. "Not on that computer"
 *   error            here would send the operator to the wrong fix.
 *   preset-missing   ONLY on `ok`: we read the list and it is not there.
 *
 * `unknown` yields null: not having asked is not evidence of anything.
 */
export type PresetButtonFault =
  | 'no-bridge' | 'no-preset' | 'lights-gone'
  | 'unauthorized' | 'unsupported' | 'error' | 'preset-missing';

export function presetButtonFault(
  cfg: LightPresetConfig | undefined,
  bridgeIds: string[] | undefined,
  st: PresetListState,
): PresetButtonFault | null {
  if (!bridgeIds || bridgeIds.length === 0) return 'no-bridge';
  if (!cfg?.presetId) return 'no-preset';
  if (cfg.target && cfg.target.kind !== 'all' && !targetIsLive(cfg.target)) return 'lights-gone';
  if (st.status === 'unauthorized') return 'unauthorized';
  if (st.status === 'unsupported') return 'unsupported';
  if (st.status === 'error') return 'error';
  if (st.status === 'ok' && !st.presets.some((p) => p.id === cfg.presetId)) return 'preset-missing';
  return null;
}

export function presetFaultLabel(fault: PresetButtonFault): string {
  switch (fault) {
    case 'no-bridge':      return 'pick a computer';
    case 'no-preset':      return 'pick a preset';
    case 'lights-gone':    return 'lights removed';
    case 'unauthorized':   return 'no lighting access';
    case 'unsupported':    return 'OPBridge too old';
    case 'error':          return 'preset list error';
    case 'preset-missing': return 'not on that computer';
  }
}

/** The fixture ids to aim at, or undefined for the whole rig. `all` (and an
 *  absent target) sends NO list, so OPBridge uses its own room order - which
 *  is what a `global` preset travels along. Zones never reach here: the
 *  picker does not offer them, and a zone target resolves to its fixture. */
export function presetFixtures(target: DmxTarget | undefined): string[] | undefined {
  if (!target || target.kind === 'all') return undefined;
  return resolveTarget(target).map((f) => f.id);
}

/** The ONE place a button's config becomes a fire body, so the button, a
 *  mod's share and any later caller cannot build it two ways. Omitted
 *  optional fields stay omitted (OPBridge refuses unknown fields and
 *  treats an absent one as its default). */
export function presetFireBody(layerId: string, cfg: LightPresetConfig): LightingFireBody {
  const fixtureIds = presetFixtures(cfg.target);
  const knobs = cfg.knobs && Object.keys(cfg.knobs).length > 0 ? cfg.knobs : undefined;
  return {
    id: layerId,
    preset: cfg.presetId,
    ...(knobs ? { knobs } : {}),
    ...(fixtureIds ? { fixtures: fixtureIds } : {}),
    ...(cfg.lengthBeats !== undefined ? { lengthBeats: cfg.lengthBeats } : {}),
  };
}

/**
 * The computer a new Light Preset button should aim at, or null to ask.
 *
 * A preset plays on the lights patched on the bridge it is SENT to, and each
 * fixture records the mesh node that outputs it (`outputNodeId`, DEC-471).
 * So when every fixture in the rig is output by one node and that node is a
 * paired bridge, that bridge is the answer - not a guess. Otherwise: the only
 * paired bridge, else null.
 */
export function autoPickPresetBridge(paired: BridgeConfig[]): string | null {
  const owners = new Set(fixtures().map((f) => f.outputNodeId).filter((n): n is string => !!n));
  if (owners.size === 1) {
    const [owner] = [...owners];
    const row = paired.find((b) => b.nodeId === owner);
    if (row) return row.nodeId ?? row.id;
  }
  if (paired.length === 1) return paired[0]!.nodeId ?? paired[0]!.id;
  return null;
}

/** One-line note for a machine in the Sends to picker: what we KNOW. */
export function presetBridgeSummary(st: PresetListState, connected = true, drivesRig = false): string {
  if (!connected) return 'offline';
  const lights = drivesRig ? 'your lights' : '';
  switch (st.status) {
    case 'ok':           return lights || `${st.presets.length} presets`;
    case 'unauthorized': return 'no lighting access';
    case 'unsupported':  return 'OPBridge too old';
    case 'error':        return 'unreachable';
    case 'unknown':      return lights || 'not checked';
  }
}

/** Test/teardown hook. */
export function _resetPresetStateForTest(): void {
  setStatesSig({});
  setLayersSig({});
}
